import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const emptyForm = {
  title: "",
  message: "",
  audience: "all",
  scheduledFor: "",
  targetUserIds: []
};

const formatDateTimeInput = value => {
  if (!value) return "";
  const date = new Date(value);
  const pad = num => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function InternalNotifications() {
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [viewsModalOpen, setViewsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const loadData = async () => {
    try {
      const [{ data: notifications }, { data: usersResponse }] = await Promise.all([
        api.get("/internal-notifications"),
        api.get("/users")
      ]);
      setRecords(Array.isArray(notifications) ? notifications : []);
      setUsers(Array.isArray(usersResponse?.users) ? usersResponse.users : []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const handleEdit = record => {
    setEditingId(record.id);
    setForm({
      title: record.title || "",
      message: record.message || "",
      audience: record.audience || "all",
      scheduledFor: formatDateTimeInput(record.scheduledFor),
      targetUserIds: Array.isArray(record.targetUserIds) ? record.targetUserIds : []
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        scheduledFor: form.scheduledFor ? new Date(form.scheduledFor).toISOString() : null
      };

      if (editingId) {
        await api.put(`/internal-notifications/${editingId}`, payload);
        toast.success("Notificacion actualizada");
      } else {
        await api.post("/internal-notifications", payload);
        toast.success("Notificacion creada");
      }

      setModalOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleArchive = async record => {
    try {
      await api.delete(`/internal-notifications/${record.id}`);
      toast.success("Notificacion archivada");
      loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenViews = record => {
    setSelectedRecord(record);
    setViewsModalOpen(true);
  };

  const handleCloseViews = () => {
    setViewsModalOpen(false);
    setSelectedRecord(null);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Title>Notificaciones internas ({records.length})</Title>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button fullWidth variant="contained" color="primary" onClick={handleOpenCreate}>
              Nueva notificacion
            </Button>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper variant="outlined" style={{ padding: 16, overflowX: "auto" }}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Desde aqui puede crear avisos internos inmediatos o programados. Los usuarios los veran en el icono de notificaciones y quedara historial.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Titulo</TableCell>
              <TableCell>Audiencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Programada</TableCell>
              <TableCell>Vistos</TableCell>
              <TableCell>Detalle</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map(record => (
              <TableRow key={record.id}>
                <TableCell>
                  <strong>{record.title}</strong>
                  <div style={{ opacity: 0.75, marginTop: 4 }}>
                    {String(record.message || "").slice(0, 110)}
                  </div>
                </TableCell>
                <TableCell>{record.audience}</TableCell>
                <TableCell>{record.status}</TableCell>
                <TableCell>{record.scheduledFor ? new Date(record.scheduledFor).toLocaleString("es-CO") : "Inmediata"}</TableCell>
                <TableCell>{record.seenCount || 0}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenViews(record)} title="Ver quiénes la vieron">
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" color="primary" onClick={() => handleEdit(record)}>
                    Editar
                  </Button>
                  {record.active && (
                    <Button size="small" color="secondary" onClick={() => handleArchive(record)}>
                      Archivar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Editar notificacion" : "Nueva notificacion"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Titulo"
                fullWidth
                variant="outlined"
                margin="dense"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mensaje"
                fullWidth
                multiline
                minRows={5}
                variant="outlined"
                margin="dense"
                value={form.message}
                onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Audiencia</InputLabel>
                <Select
                  label="Audiencia"
                  value={form.audience}
                  onChange={e => setForm(prev => ({ ...prev, audience: e.target.value }))}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="admins">Solo admins</MenuItem>
                  <MenuItem value="users">Solo usuarios operativos</MenuItem>
                  <MenuItem value="custom">Usuarios especificos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Programar para"
                type="datetime-local"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={form.scheduledFor}
                onChange={e => setForm(prev => ({ ...prev, scheduledFor: e.target.value }))}
              />
            </Grid>
            {form.audience === "custom" && (
              <Grid item xs={12}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Usuarios</InputLabel>
                  <Select
                    multiple
                    label="Usuarios"
                    value={form.targetUserIds}
                    onChange={e => setForm(prev => ({ ...prev, targetUserIds: e.target.value }))}
                    renderValue={selected =>
                      users
                        .filter(item => selected.includes(item.id))
                        .map(item => item.name)
                        .join(", ")
                    }
                  >
                    {users.map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewsModalOpen} onClose={handleCloseViews} maxWidth="sm" fullWidth>
        <DialogTitle>Usuarios que vieron la notificación</DialogTitle>
        <DialogContent dividers>
          {selectedRecord ? (
            <>
              <Typography variant="subtitle1" style={{ marginBottom: 4 }}>
                {selectedRecord.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Total vistos: {selectedRecord.seenCount || 0}
              </Typography>

              {Array.isArray(selectedRecord.seenDetails) && selectedRecord.seenDetails.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Correo</TableCell>
                      <TableCell>Fecha y hora</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedRecord.seenDetails.map(detail => (
                      <TableRow key={detail.id || `${detail.userId}-${detail.seenAt}`}>
                        <TableCell>{detail.user?.name || `Usuario #${detail.userId}`}</TableCell>
                        <TableCell>{detail.user?.email || "-"}</TableCell>
                        <TableCell>{detail.seenAt ? new Date(detail.seenAt).toLocaleString("es-CO") : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Aún no hay usuarios que hayan marcado esta notificación como vista.
                </Typography>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViews} color="primary" variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
